import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * usePaymentFailureGuard
 *
 * Prevents the user from navigating away while the payment-failure modal is active.
 *   - On open: redirects to /account/wallet once and saves the origin path.
 *   - On close (recovery): navigates back to the origin path (or /dashboard as fallback).
 *   - location.pathname is intentionally excluded from the dep array — read imperatively
 *     inside the effect to avoid re-running on every route change.
 */
const usePaymentFailureGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPaymentFailedModalOpen = useSelector((state) => state.paymentFailureReducer?.isOpen);
  const hasRedirected = useRef(false);
  const originPath = useRef(null);

  useEffect(() => {
    if (!isPaymentFailedModalOpen) {
      if (hasRedirected.current) {
        // Modal just closed after a successful recovery — send the user back
        const returnPath = originPath.current || '/dashboard';
        navigate(returnPath, { replace: true });
      }
      // Reset for next time the modal opens
      hasRedirected.current = false;
      originPath.current = null;
      return;
    }

    // Only redirect once per modal-open session to avoid infinite loops
    if (hasRedirected.current) return;

    const targetPath = '/account/wallet';
    if (location.pathname !== targetPath) {
      originPath.current = location.pathname;
      hasRedirected.current = true;
      navigate(targetPath, { replace: true });
    } else {
      // Already on the wallet page — no redirect needed, but still record that
      // we've "handled" the open so the close path knows to navigate on recovery.
      originPath.current = null; // no specific page to return to
      hasRedirected.current = true;
    }
  }, [isPaymentFailedModalOpen]); // ← intentionally NOT including location.pathname
};

export default usePaymentFailureGuard;
